/**
 * Operational logs.
 *
 * Four streams that answer four different questions, kept as tabs rather than one
 * merged feed: "what did we send?", "what broke?", "did Meta reach us?", "who changed
 * what?". Every row expands in place to its full payload - a truncated error is not
 * an error you can act on, and the whole point of this page is to stop an operator
 * having to SSH into a container to read a traceback.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui'
import { useDebounced, useQuery } from '@/hooks/useQuery'
import { api } from '@/lib/api'
import { dateTime, relativeTime, sentence } from '@/lib/format'
import type {
  AuditLogRow,
  ErrorEvent,
  LogFacets,
  LogStream,
  LogSummary,
  MessageLogItem,
  Page,
  WebhookEventRow,
} from '@/types'

const PAGE_SIZE = 50
const REFRESH_MS = 10_000

const TABS: Array<{ id: LogStream; label: string; hint: string }> = [
  { id: 'messages', label: 'Messages', hint: 'Every WhatsApp message in and out.' },
  { id: 'errors', label: 'Errors', hint: 'Warnings and failures from the API and the workers.' },
  { id: 'webhooks', label: 'Webhooks', hint: "Meta's delivery ledger, including rejected calls." },
  { id: 'audit', label: 'Audit', hint: 'Who changed what, and from where.' },
]

const WINDOW_OPTIONS = [
  { value: '1', label: 'Last hour' },
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
  { value: '', label: 'All time' },
]

export function LogsPage() {
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((t) => t.id === params.get('stream'))?.id ?? 'messages') as LogStream

  const [hours, setHours] = useState('24')
  const [live, setLive] = useState(true)

  const summary = useQuery(
    useCallback(() => api.get<LogSummary>('/logs/summary', { hours: hours || 720 }), [hours]),
    [hours],
  )
  const facets = useQuery(useCallback(() => api.get<LogFacets>('/logs/facets'), []), [])

  const setTab = (next: LogStream) => {
    const updated = new URLSearchParams(params)
    updated.set('stream', next)
    setParams(updated, { replace: true })
  }

  const shared = { hours, live, facets: facets.data, onChanged: summary.reload }

  return (
    <>
      <PageHeader
        title="Logs"
        subtitle="Everything the system sent, received, and failed at - without leaving the browser."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select label="Time window" value={hours} onChange={setHours} options={WINDOW_OPTIONS} />
            <button
              type="button"
              className={cx('btn-secondary', live && 'border-transparent')}
              style={live ? { background: 'var(--wash)', color: 'var(--series-1)' } : undefined}
              onClick={() => setLive((on) => !on)}
              aria-pressed={live}
            >
              <span aria-hidden="true">{live ? '⏸' : '▶'}</span>
              {live ? 'Live' : 'Paused'}
            </button>
          </div>
        }
      />

      {summary.data && !summary.data.persistence_enabled && (
        <div
          className="mb-4 flex items-start gap-2 rounded-card border px-4 py-3 text-sm"
          style={{
            borderColor: 'color-mix(in srgb, var(--serious) 40%, transparent)',
            background: 'color-mix(in srgb, var(--serious) 10%, transparent)',
          }}
        >
          <span aria-hidden="true" style={{ color: 'var(--serious)' }}>
            ⚠
          </span>
          {/* An empty error list must never be mistaken for a healthy system. */}
          <p className="text-ink">
            Error capture is switched off (<code>ERROR_LOG_PERSIST=false</code>), so the Errors tab
            will stay empty regardless of what the services are doing. Errors are still written to
            the container's stdout.
          </p>
        </div>
      )}

      <div
        className="mb-4 flex flex-wrap gap-1 border-b border-line"
        role="tablist"
        aria-label="Log streams"
      >
        {TABS.map((item) => {
          const counts = summary.data?.[item.id]
          const active = tab === item.id
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.id)}
              className={cx(
                'flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors',
                active
                  ? 'border-current font-medium text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )}
              style={active ? { borderColor: 'var(--series-1)' } : undefined}
            >
              {item.label}
              {counts && counts.problems > 0 ? (
                <Badge tone="critical" icon="⚠">
                  {counts.problems}
                </Badge>
              ) : counts && counts.recent > 0 ? (
                <span className="tabular text-xs text-ink-faint">{counts.recent}</span>
              ) : null}
            </button>
          )
        })}
      </div>

      <p className="mb-3 text-sm text-ink-muted">{TABS.find((t) => t.id === tab)?.hint}</p>

      {tab === 'messages' && <MessageStream {...shared} />}
      {tab === 'errors' && <ErrorStream {...shared} />}
      {tab === 'webhooks' && <WebhookStream {...shared} />}
      {tab === 'audit' && <AuditStream {...shared} />}
    </>
  )
}

/* ------------------------------------------------------------------ plumbing */

interface StreamProps {
  hours: string
  live: boolean
  facets: LogFacets | null
  onChanged: () => void
}

type Params = Record<string, string | number | boolean | undefined>

/** Fetch one page of a stream, polling while `live` and resetting offset on filter change. */
function useStream<T>(path: string, filters: Params, live: boolean) {
  const [offset, setOffset] = useState(0)
  const key = JSON.stringify(filters)

  // A filter change must not leave the reader on page 7 of a shorter result set.
  const [lastKey, setLastKey] = useState(key)
  if (key !== lastKey) {
    setLastKey(key)
    setOffset(0)
  }

  const [tick, setTick] = useState(0)
  useInterval(() => setTick((n) => n + 1), live ? REFRESH_MS : null)

  const query = useQuery<Page<T>>(
    useCallback(
      () => api.get<Page<T>>(path, { ...filters, limit: PAGE_SIZE, offset }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [path, key, offset],
    ),
    [path, key, offset, tick],
  )

  return { ...query, offset, setOffset }
}

/** setInterval as an effect; `null` delay pauses it. */
function useInterval(callback: () => void, delay: number | null) {
  const saved = useRef(callback)
  saved.current = callback
  useEffect(() => {
    if (delay === null) return
    const id = setInterval(() => saved.current(), delay)
    return () => clearInterval(id)
  }, [delay])
}

interface Column<T> {
  header: string
  width?: string
  render: (row: T) => ReactNode
}

/**
 * One table shared by all four streams: identical keyboard behaviour, identical
 * expansion, and only the columns differ.
 */
function LogTable<T extends { id: string }>({
  query,
  columns,
  detail,
  emptyTitle,
  emptyHint,
  rowTone,
}: {
  query: ReturnType<typeof useStream<T>>
  columns: Array<Column<T>>
  detail: (row: T) => ReactNode
  emptyTitle: string
  emptyHint: string
  rowTone?: (row: T) => 'critical' | 'warning' | undefined
}) {
  const [expanded, setExpanded] = useState<string | null>(null)

  if (query.loading && !query.data) return <Card><Spinner /></Card>
  if (query.error) return <Card><ErrorNote message={query.error} onRetry={query.reload} /></Card>
  if (!query.data?.items.length) {
    return (
      <Card>
        <EmptyState title={emptyTitle} hint={emptyHint} />
      </Card>
    )
  }

  const { items, total, limit, offset } = query.data

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th>{''}</Th>
            {columns.map((column) => (
              <Th key={column.header}>{column.header}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const open = expanded === row.id
            const tone = rowTone?.(row)
            return (
              <Fragment key={row.id}>
                <tr
                  className="cursor-pointer hover:bg-raised"
                  onClick={() => setExpanded(open ? null : row.id)}
                  style={
                    tone
                      ? {
                          background: `color-mix(in srgb, var(--${
                            tone === 'critical' ? 'critical' : 'serious'
                          }) 5%, transparent)`,
                        }
                      : undefined
                  }
                >
                  <Td className="w-8">
                    <button
                      type="button"
                      className="btn-ghost px-1 py-0"
                      aria-expanded={open}
                      aria-label={open ? 'Hide detail' : 'Show detail'}
                      onClick={(event) => {
                        event.stopPropagation()
                        setExpanded(open ? null : row.id)
                      }}
                    >
                      <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                    </button>
                  </Td>
                  {columns.map((column) => (
                    <Td key={column.header}>{column.render(row)}</Td>
                  ))}
                </tr>
                {open && (
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className="border-b border-line px-4 py-4"
                      style={{ background: 'var(--surface-2)' }}
                    >
                      {detail(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </Table>

      <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-xs text-ink-muted">
        <span className="tabular">
          {offset + 1}–{offset + items.length} of {total}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary px-2 py-1 text-xs"
            disabled={offset === 0}
            onClick={() => query.setOffset(Math.max(0, offset - limit))}
          >
            Newer
          </button>
          <button
            type="button"
            className="btn-secondary px-2 py-1 text-xs"
            disabled={offset + items.length >= total}
            onClick={() => query.setOffset(offset + limit)}
          >
            Older
          </button>
        </div>
      </footer>
    </Card>
  )
}

function Toolbar({ children }: { children: ReactNode }) {
  return <div className="mb-3 flex flex-wrap items-center gap-2">{children}</div>
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <input
      type="search"
      className="field w-auto min-w-[16rem] py-1.5 text-sm"
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-ink-muted">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

function Json({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined) return null
  if (typeof value === 'object' && !Object.keys(value as object).length) return null
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</p>
      <pre className="max-h-64 overflow-auto rounded-md border border-line bg-surface p-3 text-xs text-ink">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

function Facts({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2 lg:grid-cols-3">
      {rows
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <dt className="shrink-0 text-ink-faint">{label}</dt>
            <dd className="min-w-0 break-all text-ink">{value}</dd>
          </div>
        ))}
    </dl>
  )
}

function When({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-ink-faint">—</span>
  return (
    <span className="whitespace-nowrap text-xs text-ink-muted" title={dateTime(iso)}>
      {relativeTime(iso)}
    </span>
  )
}

/* ------------------------------------------------------------------ messages */

const DIRECTIONS = [
  { value: '', label: 'Any direction' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
]

const MESSAGE_STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
]

function MessageStream({ hours, live }: StreamProps) {
  const [direction, setDirection] = useState('')
  const [status, setStatus] = useState('')
  const [failedOnly, setFailedOnly] = useState(false)
  const [search, setSearch] = useState('')
  const q = useDebounced(search)

  const query = useStream<MessageLogItem>(
    '/logs/messages',
    useMemo(
      () => ({
        direction: direction || undefined,
        status: status || undefined,
        failed_only: failedOnly || undefined,
        q: q || undefined,
        hours: hours || undefined,
      }),
      [direction, status, failedOnly, q, hours],
    ),
    live,
  )

  return (
    <>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search body or message id" />
        <Select label="Direction" value={direction} onChange={setDirection} options={DIRECTIONS} />
        <Select label="Status" value={status} onChange={setStatus} options={MESSAGE_STATUSES} />
        <Toggle label="Failures only" checked={failedOnly} onChange={setFailedOnly} />
      </Toolbar>

      <LogTable
        query={query}
        emptyTitle="No messages in this window"
        emptyHint="Widen the time window, or clear the filters."
        rowTone={(row) => (row.status === 'failed' || row.status === 'blocked' ? 'critical' : undefined)}
        columns={[
          { header: 'When', render: (row) => <When iso={row.created_at} /> },
          {
            header: 'Direction',
            render: (row) => (
              <Badge tone={row.direction === 'inbound' ? 'info' : 'neutral'}>
                {row.direction === 'inbound' ? '↙ In' : '↗ Out'}
              </Badge>
            ),
          },
          {
            header: 'Contact',
            render: (row) => (
              <Link
                to={`/contacts/${row.contact_id}`}
                className="hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                <span className="block truncate font-medium text-ink">{row.contact_name}</span>
                <span className="block text-xs text-ink-faint tabular">{row.wa_id}</span>
              </Link>
            ),
          },
          {
            header: 'Body',
            render: (row) => (
              <span className="block max-w-md truncate text-ink-muted">
                {row.body || <span className="text-ink-faint">— no text —</span>}
              </span>
            ),
          },
          {
            header: 'Kind',
            render: (row) => (
              <span className="text-xs text-ink-muted">
                {row.template_name ? `Template · ${row.template_name}` : sentence(row.type)}
                {row.ai_generated && (
                  <Badge tone="info" icon="✦">
                    AI
                  </Badge>
                )}
              </span>
            ),
          },
          { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
        ]}
        detail={(row) => (
          <div className="space-y-3">
            {row.body && (
              <p className="whitespace-pre-wrap rounded-md border border-line bg-surface p-3 text-sm text-ink">
                {row.body}
              </p>
            )}
            <Facts
              rows={[
                ['Message', row.id],
                ['WhatsApp id', row.wa_message_id],
                ['Conversation', <Link key="c" className="hover:underline" to={`/conversations/${row.conversation_id}`}>{row.conversation_id}</Link>],
                ['Created', dateTime(row.created_at)],
                ['Sent', dateTime(row.sent_at)],
                ['AI run', row.ai_run_id],
              ]}
            />
            <Json label="Failure" value={row.error} />
            <Json label="Payload" value={row.payload} />
          </div>
        )}
      />
    </>
  )
}

/* -------------------------------------------------------------------- errors */

const LEVELS = [
  { value: '', label: 'Any level' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
]

function ErrorStream({ hours, live, facets }: StreamProps) {
  const [level, setLevel] = useState('')
  const [source, setSource] = useState('')
  const [search, setSearch] = useState('')
  const q = useDebounced(search)

  const sources = [
    { value: '', label: 'Any process' },
    ...(facets?.error_sources ?? []).map((value) => ({ value, label: sentence(value) })),
  ]

  const query = useStream<ErrorEvent>(
    '/logs/errors',
    useMemo(
      () => ({
        level: level || undefined,
        source: source || undefined,
        q: q || undefined,
        hours: hours || undefined,
      }),
      [level, source, q, hours],
    ),
    live,
  )

  return (
    <>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search event, message or path" />
        <Select label="Level" value={level} onChange={setLevel} options={LEVELS} />
        <Select label="Process" value={source} onChange={setSource} options={sources} />
      </Toolbar>

      <LogTable
        query={query}
        emptyTitle="Nothing has gone wrong in this window"
        emptyHint="Warnings and errors from every process land here as they happen."
        rowTone={(row) => (row.level === 'warning' ? 'warning' : 'critical')}
        columns={[
          { header: 'When', render: (row) => <When iso={row.created_at} /> },
          {
            header: 'Level',
            render: (row) => (
              <Badge
                tone={row.level === 'warning' ? 'warning' : 'critical'}
                icon={row.level === 'warning' ? '⚠' : '✕'}
              >
                {sentence(row.level)}
              </Badge>
            ),
          },
          {
            header: 'Process',
            render: (row) => <Badge tone="neutral">{sentence(row.source)}</Badge>,
          },
          {
            header: 'Event',
            render: (row) => (
              <span className="block min-w-0">
                <span className="block font-medium text-ink">{row.event}</span>
                <span className="block truncate text-xs text-ink-faint">{row.logger}</span>
              </span>
            ),
          },
          {
            header: 'Detail',
            render: (row) => (
              <span className="block max-w-md truncate text-xs text-ink-muted">
                {row.exc_type ? `${row.exc_type}: ` : ''}
                {row.message}
              </span>
            ),
          },
          {
            header: 'Path',
            render: (row) => (
              <span className="text-xs text-ink-faint">{row.path || '—'}</span>
            ),
          },
        ]}
        detail={(row) => (
          <div className="space-y-3">
            <Facts
              rows={[
                ['Time', dateTime(row.created_at)],
                ['Logger', row.logger],
                ['Exception', row.exc_type],
                [
                  'Contact',
                  row.contact_id ? (
                    <Link className="hover:underline" to={`/contacts/${row.contact_id}`}>
                      {row.contact_id}
                    </Link>
                  ) : null,
                ],
              ]}
            />
            {row.message && (
              <p className="whitespace-pre-wrap rounded-md border border-line bg-surface p-3 text-sm text-ink">
                {row.message}
              </p>
            )}
            {row.traceback && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">
                  Traceback
                </p>
                <pre className="max-h-80 overflow-auto rounded-md border border-line bg-surface p-3 text-xs leading-relaxed text-ink">
                  {row.traceback}
                </pre>
              </div>
            )}
            <Json label="Context" value={row.context} />
          </div>
        )}
      />
    </>
  )
}

/* ------------------------------------------------------------------ webhooks */

function WebhookStream({ hours, live }: StreamProps) {
  const [failedOnly, setFailedOnly] = useState(false)
  const [unprocessedOnly, setUnprocessedOnly] = useState(false)
  const [search, setSearch] = useState('')
  const q = useDebounced(search)

  const query = useStream<WebhookEventRow>(
    '/logs/webhooks',
    useMemo(
      () => ({
        failed_only: failedOnly || undefined,
        processed: unprocessedOnly ? false : undefined,
        q: q || undefined,
        hours: hours || undefined,
      }),
      [failedOnly, unprocessedOnly, q, hours],
    ),
    live,
  )

  return (
    <>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search provider event id" />
        <Toggle label="Rejected or errored" checked={failedOnly} onChange={setFailedOnly} />
        <Toggle label="Not yet processed" checked={unprocessedOnly} onChange={setUnprocessedOnly} />
      </Toolbar>

      <LogTable
        query={query}
        emptyTitle="No webhook deliveries in this window"
        emptyHint="Meta calls this endpoint for every inbound message and status change. Nothing here at all means Meta is not delivering - check the callback URL and that the app is subscribed to the WABA."
        rowTone={(row) =>
          // An 'unhandled' row carries a reason but is not a failure: Meta sent
          // something real on a field this build ignores.
          row.event_type === 'unhandled'
            ? 'warning'
            : row.error || !row.signature_valid
              ? 'critical'
              : undefined
        }
        columns={[
          { header: 'When', render: (row) => <When iso={row.created_at} /> },
          {
            header: 'Event id',
            render: (row) => (
              <span className="block max-w-xs truncate text-xs tabular text-ink">
                {row.provider_event_id}
              </span>
            ),
          },
          { header: 'Type', render: (row) => <span className="text-xs">{sentence(row.event_type)}</span> },
          {
            header: 'Signature',
            render: (row) =>
              row.signature_valid ? (
                <Badge tone="good" icon="✓">
                  Valid
                </Badge>
              ) : (
                // A bad signature is answered with a 200 so Meta stops retrying,
                // which makes this column the only place it is ever visible.
                <Badge tone="critical" icon="⊘">
                  Rejected
                </Badge>
              ),
          },
          {
            header: 'Processed',
            render: (row) =>
              row.processed_at ? (
                <When iso={row.processed_at} />
              ) : (
                <Badge tone="warning" icon="◔">
                  Pending
                </Badge>
              ),
          },
          { header: 'Attempts', render: (row) => <span className="tabular">{row.attempts}</span> },
          {
            // Named 'Reason' rather than 'Error' because it also explains the
            // deliveries that were merely ignored. Truncated here; the row detail
            // shows the sentence in full.
            header: 'Reason',
            render: (row) => (
              <span
                className="block max-w-md truncate text-xs"
                style={{
                  color: row.event_type === 'unhandled' ? 'var(--ink-muted)' : 'var(--critical)',
                }}
                title={row.error || undefined}
              >
                {row.error || ''}
              </span>
            ),
          },
        ]}
        detail={(row) => (
          <div className="space-y-3">
            <Facts
              rows={[
                ['Received', dateTime(row.created_at)],
                ['Processed', dateTime(row.processed_at)],
                ['Attempts', row.attempts],
                ['Reason', row.error],
              ]}
            />
            <Json label="Raw payload" value={row.payload} />
          </div>
        )}
      />
    </>
  )
}

/* --------------------------------------------------------------------- audit */

const ACTOR_TYPES = [
  { value: '', label: 'Any actor' },
  { value: 'admin', label: 'Admin' },
  { value: 'system', label: 'System' },
  { value: 'ai', label: 'AI' },
  { value: 'contact', label: 'Contact' },
]

function AuditStream({ hours, live, facets }: StreamProps) {
  const [actorType, setActorType] = useState('')
  const [entityType, setEntityType] = useState('')
  const [search, setSearch] = useState('')
  const q = useDebounced(search)

  const entityTypes = [
    { value: '', label: 'Any entity' },
    ...(facets?.audit_entity_types ?? []).map((value) => ({ value, label: sentence(value) })),
  ]

  const query = useStream<AuditLogRow>(
    '/logs/audit',
    useMemo(
      () => ({
        actor_type: actorType || undefined,
        entity_type: entityType || undefined,
        q: q || undefined,
        hours: hours || undefined,
      }),
      [actorType, entityType, q, hours],
    ),
    live,
  )

  return (
    <>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search action or actor" />
        <Select label="Actor" value={actorType} onChange={setActorType} options={ACTOR_TYPES} />
        <Select label="Entity" value={entityType} onChange={setEntityType} options={entityTypes} />
      </Toolbar>

      <LogTable
        query={query}
        emptyTitle="No recorded changes in this window"
        emptyHint="Every mutating action writes here, including the ones the agent takes."
        columns={[
          { header: 'When', render: (row) => <When iso={row.created_at} /> },
          {
            header: 'Action',
            render: (row) => <span className="font-medium text-ink">{row.action}</span>,
          },
          {
            header: 'Actor',
            render: (row) => (
              <span className="block min-w-0">
                <Badge tone={row.actor_type === 'admin' ? 'info' : 'neutral'}>
                  {sentence(row.actor_type)}
                </Badge>
                <span className="block truncate text-xs text-ink-faint">{row.actor_label}</span>
              </span>
            ),
          },
          {
            header: 'Entity',
            render: (row) => (
              <span className="block min-w-0">
                <span className="block text-ink">{sentence(row.entity_type)}</span>
                <span className="block truncate text-xs tabular text-ink-faint">
                  {row.entity_id}
                </span>
              </span>
            ),
          },
          { header: 'From', render: (row) => <span className="text-xs tabular">{row.ip || '—'}</span> },
        ]}
        detail={(row) => (
          <div className="space-y-3">
            <Facts
              rows={[
                ['Time', dateTime(row.created_at)],
                ['Actor id', row.actor_id],
                ['IP', row.ip],
                ['User agent', row.user_agent],
              ]}
            />
            <div className="grid gap-3 lg:grid-cols-2">
              <Json label="Before" value={row.before} />
              <Json label="After" value={row.after} />
            </div>
            <Json label="Context" value={row.context} />
          </div>
        )}
      />
    </>
  )
}
